import React, { useCallback } from "react";
import { IoAdd, IoBuild } from "react-icons/io5";
import WorkOrderList from "./List.js";
import WorkOrderForm from "./Form.js";
import { NavLink, useParams, useNavigate } from "react-router-dom";

import { readAuthUser } from "../../../../utils/authUser";
import { AccessDenied } from "../../../../shared-ui/States";

// --- DYNAMIC PERMISSION LOGIC ---
const usePermissions = () => {
  const authData = readAuthUser();

  const hasPermission = useCallback(
    (requiredPermission) => {
      if (authData.isSuperAdmin) return true;
      if (Array.isArray(requiredPermission)) {
        return requiredPermission.some((p) => authData.permissions.includes(p));
      }
      return authData.permissions.includes(requiredPermission);
    },
    [authData.isSuperAdmin, authData.permissions]
  );

  const canAccessModule = useCallback(() => {
    if (authData.isSuperAdmin) return true;
    const workOrderPermissions = [
      "CREATE WORK ORDER",
      "VIEW WORK ORDER",
      "SET WORK ORDER",
      "DOWNLOAD WORK ORDER PDF",
      "VIEW ALL WORK ORDERS",
    ];
    return workOrderPermissions.some((p) => authData.permissions.includes(p));
  }, [authData.isSuperAdmin, authData.permissions]);

  return { user: authData.user, hasPermission, canAccessModule };
};
// --- END PERMISSION LOGIC ---

const PlusIcon = () => <IoAdd className="icon" aria-hidden="true" />;

function WorkOrder() {
  const { hasPermission, canAccessModule } = usePermissions();

  if (!canAccessModule()) {
    return (
      <div className="projects-page crane-page">
        <AccessDenied description="You do not have permission to view the Work Orders module." />
      </div>
    );
  }

  return (
    <div className="projects-page">
      <header className="page-header-projects">
        <div className="header-text-content">
          <h1>Work Order Management</h1>
          <p>Manage work orders efficiently.</p>
        </div>
        {hasPermission("CREATE WORK ORDER") && (
          <NavLink to="/work-orders/new">
            <button className="create-project-button-header">
              <PlusIcon /> Create New Work Order
            </button>
          </NavLink>
        )}
      </header>

      <WorkOrderList />
    </div>
  );
}

export const WorkOrderFormPage = () => {
  let { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const workOrderId = id === "new" ? null : id;
  const isCreating = workOrderId === null;

  React.useEffect(() => {
    // Redirect if user tries to create without permission
    if (isCreating && !hasPermission("CREATE WORK ORDER")) {
      navigate("/access-denied", { replace: true });
    }
    // Redirect if user tries to edit without permission.
    else if (!isCreating && !hasPermission("SET WORK ORDER")) {
      navigate("/access-denied", { replace: true });
    }
  }, [id, isCreating, hasPermission, navigate]);

  // Render nothing while redirecting. This prevents a flicker of the form content.
  if (
    (isCreating && !hasPermission("CREATE WORK ORDER")) ||
    (!isCreating && !hasPermission("SET WORK ORDER"))
  ) {
    return null;
  }

  // If the checks pass, render the form page.
  return (
    <div className="projects-page">
      <header className="page-header-projects">
        <NavLink to="/work-orders">
          <button className="backbutton">Back To Work Orders</button>
        </NavLink>
      </header>
      <WorkOrderForm workOrderId={workOrderId} />
    </div>
  );
};

export default WorkOrder;
